import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, mergeMap, of, switchMap} from 'rxjs';
import {whenRouterHasNavigatedTo, withLatestFromSelectors} from '@store/common/effects.helpers';
import {AccountSelectors, PatronSelectors, RouterSelectors} from '@store/store.selectors';
import {AccountActions} from './account.actions';
import {AccountService} from './account.service';
import {RouterActions} from '@store/router/router.actions';
import {MatDialog} from '@angular/material/dialog';
import {MakeAPaymentDialog, MakeAPaymentDialogData} from '../../core/profile/balance-and-payments/dialogs/make-a-payment.dialog';
import {SquareAppDetails} from '@store/account/account.types';
import {PaymentConfirmationDialog, PaymentConfirmationDialogData} from '../../core/profile/balance-and-payments/dialogs/payment-confirmation.dialog';

@Injectable({providedIn: 'root'})
export class AccountEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly dialog = inject(MatDialog);
  private readonly service: AccountService = inject(AccountService);

  public redirectToFirstTabOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('PROFILE'),
      map(() => RouterActions.replaceRouteWith('PROFILE_ACCOUNT'))
    )
  );

  public loadBalanceInfoAndTransactionsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('PROFILE_BALANCE'),
      mergeMap(() => [
        AccountActions.loadBalanceTransactions(),
        AccountActions.loadSquareAppDetails()
      ])
    )
  );

  public loadBalanceTransactions = createEffect(() =>
    this.actions.pipe(
      ofType(AccountActions.loadBalanceTransactions, AccountActions.submitMakeAPaymentSuccess),
      withLatestFromSelectors(
        RouterSelectors.queryParams,
        AccountSelectors.BalanceTransactions.includeAllTransactions
      ),
      switchMap(([, queryParams, includeAllTransactions]) => this.service.loadBalanceTransactions(queryParams, includeAllTransactions).pipe(
        map(response => AccountActions.loadBalanceTransactionsSuccess(response)),
        catchError((error: unknown) => of(AccountActions.loadBalanceTransactionsFailure(error)))
      ))
    )
  );

  public loadBalanceTransactionsOnToggleIncludeAll = createEffect(() =>
    this.actions.pipe(
      ofType(AccountActions.toggleIncludeAllBalanceTransactions),
      map(() => AccountActions.loadBalanceTransactions())
    )
  );

  public loadSquareAppDetails = createEffect(() =>
    this.actions.pipe(
      ofType(AccountActions.loadSquareAppDetails),
      switchMap(() => this.service.loadSquareAppDetails().pipe(
        map((response: SquareAppDetails) => AccountActions.loadSquareAppDetailsSuccess(response)),
        catchError((error: unknown) => of(AccountActions.loadSquareAppDetailsFailure(error)))
      ))
    )
  );

  public showMakeAPayment = createEffect(() =>
    this.actions.pipe(
      ofType(AccountActions.showMakeAPayment),
      withLatestFromSelectors(PatronSelectors.AccountStatus.currentAccountBalance, AccountSelectors.SquareApp.details),
      map(([, currentAccountBalance, squareAppDetails]) =>
        this.dialog.open<MakeAPaymentDialog, MakeAPaymentDialogData>(
          MakeAPaymentDialog,
          {data: {squareAppDetails, currentAccountBalance}, disableClose: true}
        ))
    ), {dispatch: false}
  );

  submitMakeAPayment = createEffect(() =>
    this.actions.pipe(
      ofType(AccountActions.submitMakeAPayment),
      switchMap(({value}) => this.service.submitMakeAPayment(value).pipe(
        mergeMap((transaction) => [
          AccountActions.showPaymentConfirmation({...value, transactionId: String(transaction.id)}),
          AccountActions.submitMakeAPaymentSuccess()
        ]),
        catchError((error: unknown) => of(AccountActions.submitMakeAPaymentFailure(error)))
      ))
    )
  );

  public showPaymentConfirmation = createEffect(() =>
    this.actions.pipe(
      ofType(AccountActions.showPaymentConfirmation),
      map(({value}) =>
        this.dialog.open<PaymentConfirmationDialog, PaymentConfirmationDialogData>(
          PaymentConfirmationDialog,
          {data: {value}, disableClose: true}
        )),
    ), {dispatch: false}
  );
}
